import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_PATHS } from '../config/api-paths';
import { RepresentativesApi } from '../tokens/representatives.token';
import { RepView, RepCreate, RepCreateResult, RepPatch, GamView } from '../models/sme-representative.model';

/** The dedicated SCA header (never `Authorization`). */
const SCA_TOKEN_HEADER = 'X-SCA-Token';

/**
 * REST-only implementation of `RepresentativesApi` (Story E / EM-472, PR-02).
 *
 * Calls (party-scoped):
 *   GET    /services/embedded/api/parties/{partyId}/representatives
 *   POST   /services/embedded/api/parties/{partyId}/representatives          (+ X-SCA-Token: manage_rep)
 *   PATCH  /services/embedded/api/parties/{partyId}/representatives/{repId}  (+ X-SCA-Token)
 *   DELETE /services/embedded/api/parties/{partyId}/representatives/{repId}  (+ X-SCA-Token)
 *
 * Rides the Keycloak bearer (`authInterceptor`); the SCA token is attached on the three
 * mutations. Rule 07 / Rule 10 — no Mock.
 */
@Injectable()
export class RepresentativesRestService implements RepresentativesApi {
  private readonly http = inject(HttpClient);
  private readonly base = API_PATHS.PARTIES;

  list(partyId: number): Observable<RepView[]> {
    return this.http.get<RepView[]>(`${this.base}/${partyId}/representatives`);
  }

  add(partyId: number, request: RepCreate, scaToken: string): Observable<RepCreateResult> {
    return this.http.post<RepCreateResult>(
      `${this.base}/${partyId}/representatives`,
      request,
      { headers: this.scaHeaders(scaToken) },
    );
  }

  update(partyId: number, repId: number, patch: RepPatch, scaToken: string): Observable<RepView> {
    return this.http.patch<RepView>(
      `${this.base}/${partyId}/representatives/${repId}`,
      patch,
      { headers: this.scaHeaders(scaToken) },
    );
  }

  revoke(partyId: number, repId: number, scaToken: string): Observable<void> {
    return this.http.delete<void>(
      `${this.base}/${partyId}/representatives/${repId}`,
      { headers: this.scaHeaders(scaToken) },
    );
  }

  /**
   * POST /parties/{partyId}/representatives/{repId}/reinvite (+ X-SCA-Token) — EM-936 / E7.
   * Same SCA-gated mutation shape as add / revoke, because re-issuing a credential path is one.
   */
  reinvite(partyId: number, repId: number, scaToken: string): Observable<RepCreateResult> {
    return this.http.post<RepCreateResult>(
      `${this.base}/${partyId}/representatives/${repId}/reinvite`,
      {},
      { headers: this.scaHeaders(scaToken) },
    );
  }

  // ---- Grace Account Manager management (EM-935 / D6) ----
  //   GET    /services/embedded/api/parties/{partyId}/representatives/gams
  //   POST   /services/embedded/api/parties/{partyId}/representatives/{repId}/gam  (+ X-SCA-Token)
  //   DELETE /services/embedded/api/parties/{partyId}/representatives/{repId}/gam  (+ X-SCA-Token)

  listGams(partyId: number): Observable<GamView[]> {
    return this.http.get<GamView[]>(`${this.base}/${partyId}/representatives/gams`);
  }

  promoteToGam(partyId: number, repId: number, scaToken: string): Observable<GamView> {
    return this.http.post<GamView>(
      `${this.base}/${partyId}/representatives/${repId}/gam`,
      {},
      { headers: this.scaHeaders(scaToken) },
    );
  }

  demoteFromGam(partyId: number, repId: number, scaToken: string): Observable<void> {
    return this.http.delete<void>(
      `${this.base}/${partyId}/representatives/${repId}/gam`,
      { headers: this.scaHeaders(scaToken) },
    );
  }

  private scaHeaders(scaToken: string): HttpHeaders {
    return new HttpHeaders().set(SCA_TOKEN_HEADER, scaToken);
  }
}
